/**
 * 🧭 Directions Page
 * Walking directions with compass
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowUp, Navigation } from 'lucide-react';
import styles from './Directions.module.css';

const DESTINATIONS: Record<string, { name: string; details: string; icon: string }> = {
  bathroom: { name: 'Restroom A', details: 'Ground Level', icon: '🚻' },
  food: { name: 'Frosty Mango', details: 'Ground Level, near Gate B', icon: '🍦' },
  exit: { name: 'Exit 4', details: 'Ground Level', icon: '🚪' },
  seat: { name: 'Your Seat', details: 'Section GA-12, Row K', icon: '🎫' },
  accessible: { name: 'Accessible Restroom', details: 'Level 1', icon: '♿' },
};

export function DirectionsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'food';
  
  const [distance, setDistance] = useState(47);
  const [heading, setHeading] = useState(0);
  const [arrived, setArrived] = useState(false);
  
  const destination = DESTINATIONS[type] || DESTINATIONS.food;

  useEffect(() => {
    // Request compass permission and track heading
    if ('DeviceOrientationEvent' in window) {
      const handleOrientation = (event: DeviceOrientationEvent) => {
        if (event.alpha !== null) {
          setHeading(event.alpha);
        }
      };
      
      // For iOS 13+
      if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
        (DeviceOrientationEvent as any).requestPermission()
          .then((response: string) => {
            if (response === 'granted') {
              window.addEventListener('deviceorientation', handleOrientation);
            }
          });
      } else {
        window.addEventListener('deviceorientation', handleOrientation);
      }
      
      return () => {
        window.removeEventListener('deviceorientation', handleOrientation);
      };
    }
  }, []);

  // Simulate distance countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setDistance((d) => {
        if (d <= 5) {
          setArrived(true);
          clearInterval(interval);
          return 0;
        }
        return d - 3;
      });
    }, 1500);
    
    return () => clearInterval(interval);
  }, []);

  const getDirection = () => {
    const angle = heading % 360;
    if (angle < 45 || angle >= 315) return 'AHEAD';
    if (angle < 135) return 'RIGHT';
    if (angle < 225) return 'BEHIND';
    return 'LEFT';
  };

  if (arrived) {
    return (
      <div className={styles.arrivedContainer}>
        <div className={styles.arrivedContent}>
          <span className={styles.arrivedEmoji}>🎉</span>
          <h1 className={styles.arrivedTitle}>You've Arrived!</h1>
          <p className={styles.arrivedName}>{destination.name}</p>
          <p className={styles.arrivedDetails}>{destination.details}</p>
        </div>
        <button className={styles.doneButton} onClick={() => navigate(-1)}>
          Done
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <h1 className={styles.title}>Directions</h1>
        <div style={{ width: 24 }} />
      </header>

      {/* Destination Card */}
      <div className={styles.destinationCard}>
        <span className={styles.destIcon}>{destination.icon}</span>
        <div className={styles.destInfo}>
          <span className={styles.destName}>{destination.name}</span>
          <span className={styles.destDetails}>{destination.details}</span>
        </div>
      </div>

      {/* Compass */}
      <div className={styles.compassContainer}>
        <div 
          className={styles.arrow}
          style={{ transform: `rotate(${45 - heading}deg)` }}
        >
          <ArrowUp size={120} />
        </div>
        
        <div className={styles.distanceContainer}>
          <span className={styles.distance}>{distance}</span>
          <span className={styles.distanceUnit}>meters</span>
        </div>
        
        <span className={styles.direction}>{getDirection()}</span>
      </div>

      {/* Steps */}
      <div className={styles.stepsContainer}>
        <h3 className={styles.stepsTitle}>STEPS</h3>
        <div className={styles.step}>
          <span className={styles.stepNumber}>1</span>
          <span>Head toward main concourse</span>
        </div>
        <div className={styles.step}>
          <span className={styles.stepNumber}>2</span>
          <span>Turn right at Gate B</span>
        </div>
        <div className={styles.step}>
          <span className={styles.stepNumber}>3</span>
          <span>{destination.name} on your left</span>
        </div>
      </div>

      <button className={styles.cancelButton} onClick={() => navigate(-1)}>
        Cancel Navigation
      </button>
    </div>
  );
}
